/**
 * A quest as the form holds it.
 *
 * A step is three things: the line the journal shows, what finishes it, and
 * where it points. The first is a sentence, the second is a condition of the
 * same kind a conversation uses, and the third is something in the world. Kept
 * apart from the panel so the panel is about asking the questions.
 */

import { SKILL_KEYS } from '@inverse/common';
import type { Condition, Effect } from './Dialogue';

/** What finishes a step, in the shapes the form offers rather than in general. */
export type TriggerKind =
  | 'none'
  | 'carry'
  | 'kill'
  | 'level'
  | 'flag'
  | 'deed'
  | 'place'
  | 'wearing'
  | 'written';

export const TRIGGER_LABELS: Readonly<Record<TriggerKind, string>> = {
  none: 'Dialogue',
  carry: 'Carry Item',
  kill: 'Kill Character',
  level: 'Reach Level',
  flag: 'Flag Set',
  deed: 'Perform Action',
  place: 'Reach Location',
  wearing: 'Equip Item',
  written: 'Anything Else',
};

export const TRIGGER_ABOUT: Readonly<Record<TriggerKind, string>> = {
  none: 'Moved along by a line of conversation, or by hand.',
  carry: 'Done the moment they have enough of it in the bag.',
  kill: 'Counts the kills of one kind of character, however long it takes.',
  level: 'Done when a skill reaches the level named.',
  flag: 'For anything the rest of the game writes down about somebody.',
  deed: 'Done once they have done the thing enough times: lit a fire, cooked a fish, chopped a log.',
  place: 'Done when they stand anywhere inside the ground named.',
  wearing: 'Done while they are wearing it, which is not the same as carrying it.',
  written:
    'Whatever you write, with the same builder everything else uses: the hour, a world flag, what is standing on a tile, a chance.',
};

export const SKILLS = SKILL_KEYS;

/** Where something worn has to be: in a gear slot, or on the belt. */
export type WornPlace = 'gear' | 'belt';

export const WORN_LABELS: Readonly<Record<WornPlace, string>> = {
  gear: 'Gear',
  belt: 'Toolbelt',
};

/** A condition as the content file holds it. Only the fields a form fills in. */
/*
 * What a step waits for, which is the same thing anything else waits for.
 *
 * This used to be a shorter list of its own: the handful of questions the quest
 * form had boxes for, and nothing else. The conditions the rest of the game
 * asks grew past it, so a step told to wait on the hour, on a world flag, on
 * what is standing on a tile or on a chance was written out as an empty goal
 * and read back as one. One shape, shared with the builder that edits it, is
 * what stops that happening again.
 */
export type SavedCondition = Condition;

/** Where finishing a step leads, when it does not simply lead on. */
export interface SavedBranch {
  when?: SavedCondition[];
  /** Counted from one, the way the journal counts. Nought finishes the quest. */
  step: number;
}

export interface SavedStep {
  text: string;
  done?: SavedCondition[];
  branches?: SavedBranch[];
  /** What happens the moment it is finished. */
  does?: Effect[];
  at?: {
    npcId?: number;
    objectId?: number;
    itemId?: number;
    tx?: number;
    tz?: number;
    plane?: number;
    /** Named ground it points at, by the number the place carries. */
    inPlace?: number;
    note?: string;
  };
}

export interface SavedQuest {
  id: string;
  name: string;
  summary: string;
  /** The line that says where it starts, for somebody who has not started it. */
  hint?: string;
  /** What it asks of a player in so many words, on top of what is worked out. */
  requirements?: string[];
  /** What it asks that the world actually checks before letting it begin. */
  needs?: SavedCondition[];
  /** Kept out of the journal until all of these are true. */
  hiddenUntil?: Condition[];
  /** Whether taking it on follows it without being asked. */
  followed?: boolean;
  /** What the notice says when it is finished, in the quest's own voice. */
  finishedText?: string;
  /** What it gave, in the words whoever wrote it chose. */
  rewards?: { text: string; itemId?: number; quantity?: number }[];
  /** What happens the moment the whole quest is finished. */
  does?: Effect[];
  steps: (SavedStep | string)[];
}

export interface StepDraft {
  text: string;
  trigger: TriggerKind;
  /** What the trigger is about: an item, a character, a skill or a mark. */
  itemId: string;
  npcId: string;
  count: string;
  skill: (typeof SKILLS)[number];
  level: string;
  flag: string;
  flagIs: string;
  /**
   * Whatever somebody wrote, for the step that waits on something unnamed.
   *
   * The seven named triggers are the questions common enough to be worth a box
   * of their own. This is every other question a condition can ask.
   */
  written: SavedCondition[];
  /** What a Deed Goal watches, named by the skill it took. */
  deed: string;
  /**
   * More things an Item Goal asks for, each with how it joins the one before.
   *
   * "Two logs and a rope" and "either a hatchet or an axe" are both ordinary
   * things to ask of somebody, and one item with one count could say neither.
   */
  alsoCarry: { itemId: string; count: string; or: boolean }[];
  /**
   * Where the first thing an Equipment Goal asks for has to be.
   *
   * A pickaxe held as a weapon and a pickaxe on the belt are two different
   * things somebody can have done, so the goal says which it means.
   */
  wornOn: WornPlace;
  /**
   * More things an Equipment Goal asks for, each with its own place.
   *
   * "A hatchet in hand and a tinderbox on the belt" is one thing to ask of
   * somebody, and one item could not say it.
   */
  alsoWear: { itemId: string; on: WornPlace; or: boolean }[];
  deedOf: string;
  /** The ground a Location Goal is about. */
  areaTx: string;
  areaTz: string;
  areaWidth: string;
  areaHeight: string;
  /**
   * A second thing a dialogue goal asks for.
   *
   * Talking to somebody while carrying or wearing a particular thing is one
   * goal, not two: the conversation is what finishes it, and the item is what
   * the conversation is about.
   */
  alsoItem: string;
  alsoWorn: string;
  /** Where the step points, for whoever is following it. */
  markNpcId: string;
  markObjectId: string;
  markItemId: string;
  markTx: string;
  markTz: string;
  /** Named ground to point at, by the number the place carries. */
  markPlace: string;
  note: string;
  /**
   * Where finishing this step leads, when it does not simply lead on.
   *
   * A flag and the step to go to, because a flag is what a conversation writes
   * down when somebody answers one way rather than another, and answering is
   * what most forks in a quest are made of.
   */
  branches: { flag: string; is: string; step: string }[];
  /**
   * What happens the moment this step is finished.
   *
   * The same effects a line of conversation carries. Kept as the effects
   * themselves rather than as fields of their own, because the editor for them
   * already exists and a second one would drift from it.
   */
  does: Effect[];
}

/** One thing a quest asks before it begins, in the shapes the panel offers. */
export interface NeedDraft {
  kind: 'quest' | 'skill';
  /** The quest that has to be finished first. */
  quest: string;
  skill: (typeof SKILLS)[number];
  level: string;
}

export interface QuestDraft {
  id: string;
  name: string;
  summary: string;
  hint: string;
  /** One per line, which is how they are typed and how they are read back. */
  requirements: string;
  /** The same thing again, in the form the world can check. */
  needs: NeedDraft[];
  /** Kept out of the journal until these are true: a quest nobody could find yet. */
  hiddenUntil: Condition[];
  /** Whether taking it on follows it without being asked. */
  followed: boolean;
  /** What the notice says when it is finished. */
  finishedText: string;
  /** What it gave, one a line: a count, an item name, or plain words. */
  rewards: string;
  /**
   * What happens the moment the quest is finished.
   *
   * Handing the reward over, in other words, and anything else the end of a
   * quest is meant to do. Kept apart from the words above on purpose: what the
   * notice reads out is prose somebody wrote, and what the world does about it
   * is a list of effects.
   */
  does: Effect[];
  steps: StepDraft[];
}

const number = (text: string, fallback = 0): number => {
  const held = Number(text);
  return Number.isFinite(held) ? held : fallback;
};

export function blankStep(text = 'And then?'): StepDraft {
  return {
    text,
    does: [],
    trigger: 'none',
    itemId: '0',
    npcId: '0',
    count: '1',
    skill: 'woodcutting',
    level: '5',
    flag: '',
    flagIs: '',
    deed: 'cooking',
    deedOf: '0',
    written: [],
    alsoCarry: [],
    wornOn: 'gear',
    alsoWear: [],
    areaTx: '0',
    areaTz: '0',
    areaWidth: '3',
    areaHeight: '3',
    alsoItem: '0',
    alsoWorn: '0',
    markNpcId: '0',
    markObjectId: '0',
    markItemId: '0',
    markTx: '0',
    markTz: '0',
    markPlace: '0',
    note: '',
    branches: [],
  };
}

export function blankQuest(name: string): QuestDraft {
  return {
    id: name
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '')
      .slice(0, 48),
    name: name.trim(),
    summary: '',
    hint: '',
    requirements: '',
    needs: [],
    hiddenUntil: [],
    followed: false,
    finishedText: '',
    rewards: '',
    does: [],
    steps: [blankStep('Find out more.')],
  };
}

/** Reads a step out of the file, whichever of the two shapes it is written in. */
function stepDraftFrom(held: SavedStep | string): StepDraft {
  if (typeof held === 'string') return blankStep(held);

  const draft = blankStep(held.text);
  draft.markPlace = String(held.at?.inPlace ?? 0);
  draft.does = held.does ?? [];
  draft.branches = (held.branches ?? []).map((branch) => ({
    flag: branch.when?.[0]?.flag ?? '',
    is: branch.when?.[0]?.is ?? '',
    step: String(branch.step),
  }));

  const first = held.done?.[0];

  if (first?.item !== undefined) {
    draft.trigger = 'carry';
    draft.itemId = String(first.item);
    draft.count = String(first.count ?? 1);
    // Everything else it asks to be carrying, in the order it was written.
    draft.alsoCarry = (held.done ?? [])
      .slice(1)
      .filter((one) => one.item !== undefined)
      .map((one) => ({
        itemId: String(one.item ?? 0),
        count: String(one.count ?? 1),
        or: one.or === true,
      }));
  } else if (first?.killed !== undefined) {
    draft.trigger = 'kill';
    draft.npcId = String(first.killed);
    draft.count = String(first.atLeastKilled ?? 1);
  } else if (first?.skill !== undefined) {
    draft.trigger = 'level';
    draft.skill = (first.skill as (typeof SKILLS)[number]) ?? 'woodcutting';
    draft.level = String(first.level ?? 2);
  } else if (first?.inArea !== undefined) {
    draft.trigger = 'place';
    draft.areaTx = String(first.inArea.tx);
    draft.areaTz = String(first.inArea.tz);
    draft.areaWidth = String(first.inArea.width);
    draft.areaHeight = String(first.inArea.height);
  } else if (first?.wearing !== undefined) {
    draft.trigger = 'wearing';
    draft.itemId = String(first.wearing);
    draft.wornOn = first.onBelt === true ? 'belt' : 'gear';
    // And everything else it asks to be wearing, in the order it was written.
    draft.alsoWear = (held.done ?? [])
      .slice(1)
      .filter((one) => one.wearing !== undefined)
      .map((one) => ({
        itemId: String(one.wearing ?? 0),
        on: one.onBelt === true ? 'belt' : 'gear',
        or: one.or === true,
      }));
  } else if (first?.deed !== undefined) {
    draft.trigger = 'deed';
    draft.deed = first.deed;
    draft.deedOf = String(first.deedOf ?? 0);
    draft.count = String(first.atLeastDone ?? 1);
  } else if (first?.flag !== undefined) {
    // A mark left by reaching a line of conversation is a dialogue goal, not a
    // flag goal. Flags are for facts about a player, such as whether they have
    // ever lit a fire; how far along a conversation they are is what the
    // dialogue goal is for, and reading one back as the other is how a step
    // written in the conversation editor came back looking like something else.
    const spoken = /^said\.(\d+)\./.exec(first.flag);

    draft.trigger = spoken ? 'none' : 'flag';
    draft.flag = first.flag;
    draft.flagIs = first.is ?? '';
  } else if (first !== undefined) {
    /*
     * Something none of the named triggers is about.
     *
     * The hour, a world flag, a status, what is standing on a tile, a chance.
     * Read back as written rather than silently dropped: a step told to wait on
     * one of these used to come back blank, which is the whole of why a step
     * could only ever ask the seven questions above.
     */
    draft.trigger = 'written';
    draft.written = held.done ?? [];
  }

  // A dialogue goal can also ask for something carried or worn: those ride
  // along as extra conditions rather than as a goal of their own.
  if (draft.trigger === 'none') {
    for (const condition of held.done ?? []) {
      if (condition === first) continue;
      if (condition.item !== undefined) draft.alsoItem = String(condition.item);
      if (condition.wearing !== undefined) draft.alsoWorn = String(condition.wearing);
    }
  }

  draft.markNpcId = String(held.at?.npcId ?? 0);
  draft.markObjectId = String(held.at?.objectId ?? 0);
  draft.markItemId = String(held.at?.itemId ?? 0);
  draft.markTx = String(held.at?.tx ?? 0);
  draft.markTz = String(held.at?.tz ?? 0);
  draft.note = held.at?.note ?? '';

  return draft;
}

export function draftFrom(quest: SavedQuest): QuestDraft {
  return {
    id: quest.id,
    name: quest.name,
    summary: quest.summary,
    hint: quest.hint ?? '',
    requirements: (quest.requirements ?? []).join('\n'),
    hiddenUntil: [...(quest.hiddenUntil ?? [])],
    followed: quest.followed === true,
    finishedText: quest.finishedText ?? '',
    rewards: (quest.rewards ?? [])
      .map((reward) =>
        reward.quantity && reward.quantity > 1
          ? `${reward.quantity} x ${reward.text}`
          : reward.text,
      )
      .join('\n'),
    does: quest.does ?? [],
    needs: (quest.needs ?? []).map((need) => ({
      kind: need.quest !== undefined ? ('quest' as const) : ('skill' as const),
      quest: need.quest ?? '',
      skill: (need.skill as (typeof SKILLS)[number]) ?? 'woodcutting',
      level: String(need.level ?? 1),
    })),
    steps: quest.steps.map(stepDraftFrom),
  };
}

/** The condition a step's goal comes to, or nothing for a step nobody watches. */
function conditionOf(step: StepDraft): SavedCondition[] {
  const held: SavedCondition[] = [];

  switch (step.trigger) {
    case 'carry':
      held.push({
        item: Math.max(0, Math.round(number(step.itemId))),
        count: Math.max(1, Math.round(number(step.count, 1))),
      });

      // And whatever else it asks for, each saying how it joins what came
      // before. All in one bracket, which is what a list of things to bring is.
      for (const also of step.alsoCarry) {
        held.push({
          item: Math.max(0, Math.round(number(also.itemId))),
          count: Math.max(1, Math.round(number(also.count, 1))),
          or: also.or,
          group: 0,
        });
      }
      break;
    case 'kill':
      held.push({
        killed: Math.max(0, Math.round(number(step.npcId))),
        atLeastKilled: Math.max(1, Math.round(number(step.count, 1))),
      });
      break;
    case 'level':
      held.push({
        skill: step.skill,
        level: Math.min(99, Math.max(1, Math.round(number(step.level, 2)))),
      });
      break;
    case 'place':
      held.push({
        inArea: {
          tx: Math.round(number(step.areaTx)),
          tz: Math.round(number(step.areaTz)),
          width: Math.min(128, Math.max(1, Math.round(number(step.areaWidth, 1)))),
          height: Math.min(128, Math.max(1, Math.round(number(step.areaHeight, 1)))),
          plane: 0,
        },
      });
      break;
    case 'wearing':
      held.push({
        wearing: Math.max(0, Math.round(number(step.itemId))),
        ...(step.wornOn === 'belt' ? { onBelt: true } : {}),
      });

      // And whatever else it asks to be wearing, each saying how it joins what
      // came before. All in one bracket, which is what a list of things to have
      // on is.
      for (const also of step.alsoWear) {
        held.push({
          wearing: Math.max(0, Math.round(number(also.itemId))),
          ...(also.on === 'belt' ? { onBelt: true } : {}),
          or: also.or,
          group: 0,
        });
      }
      break;
    case 'flag':
      if (step.flag.trim().length > 0) {
        held.push({
          flag: step.flag.trim(),
          ...(step.flagIs.trim() ? { is: step.flagIs.trim() } : {}),
        });
      }
      break;
    /*
     * Whatever somebody wrote, as written.
     *
     * The seven above are the questions worth a name of their own, and they
     * were also the only questions a step could ask: the conditions grew a good
     * deal without this list following them, so a step could not wait on the
     * hour, a world flag, a status, what is standing on a tile, or a chance,
     * every one of which anything else in the game can wait on.
     */
    case 'written':
      return step.written.slice(0, 8);
    case 'none':
      // The line of conversation that finishes it, which is the whole of what a
      // dialogue goal is. This was falling through to the default and being
      // dropped, so a step told which line to wait for forgot it the moment it
      // was written down, and the slot came back empty.
      if (step.flag.trim().length > 0) {
        held.push({
          flag: step.flag.trim(),
          is: step.flagIs.trim() || 'yes',
        });
      }
      break;
    case 'deed':
      held.push({
        deed: step.deed,
        deedOf: Math.max(0, Math.round(number(step.deedOf))),
        atLeastDone: Math.max(1, Math.round(number(step.count, 1))),
      });
      break;
    default:
      break;
  }

  // What a conversation is about, when the goal is a conversation: talking to
  // somebody while carrying the amulet is one goal, not two.
  if (step.trigger === 'none') {
    const carried = Math.round(number(step.alsoItem));
    const worn = Math.round(number(step.alsoWorn));
    if (carried > 0) held.push({ item: carried, count: 1 });
    if (worn > 0) held.push({ wearing: worn });
  }

  return held;
}

/** The lines typed into the requirements box, tidied and capped. */
function requirementsOf(draft: QuestDraft): string[] | undefined {
  const lines = draft.requirements
    .split('\n')
    .map((line) => line.trim().slice(0, 120))
    .filter((line) => line.length > 0)
    .slice(0, 12);

  return lines.length > 0 ? lines : undefined;
}

/** What a quest asks for, in the form the world can actually check. */
function needsOf(draft: QuestDraft): SavedCondition[] | undefined {
  const held: SavedCondition[] = draft.needs
    .map((need) =>
      need.kind === 'quest'
        ? { quest: need.quest.trim(), finished: true }
        : {
            skill: need.skill,
            level: Math.max(1, Math.min(99, Math.round(number(need.level, 1)))),
          },
    )
    .filter((one) => !('quest' in one) || (one.quest ?? '').length > 0)
    .slice(0, 8);

  return held.length > 0 ? held : undefined;
}

/** The quest the server is asked to keep, built from what was typed. */
export function questFrom(draft: QuestDraft): SavedQuest {
  return {
    id: draft.id,
    name: draft.name.trim(),
    summary: draft.summary.slice(0, 300),
    hint: draft.hint.trim().slice(0, 200) || undefined,
    requirements: requirementsOf(draft),
    needs: needsOf(draft),
    hiddenUntil: draft.hiddenUntil.slice(0, 8),
    followed: draft.followed,
    finishedText: draft.finishedText.slice(0, 300),
    // One a line, and a number in front of one means how many of it. Written
    // this way because a reward is a sentence somebody wrote, not a row in a
    // table: "Nine hundred Woodcutting experience" is a reward, and there is no
    // item id for it.
    rewards: draft.rewards
      .split('\n')
      .map((line) => line.trim())
      .filter((line) => line.length > 0)
      .slice(0, 12)
      .map((line) => {
        const counted = /^(\d+)\s*(?:x\s*)?(.+)$/.exec(line);
        return counted
          ? { text: counted[2] ?? line, quantity: Math.min(1_000_000, Number(counted[1])) }
          : { text: line.slice(0, 80) };
      }),
    does: draft.does.slice(0, 16),
    steps: draft.steps.map((step) => ({
      text: step.text.slice(0, 200),
      done: conditionOf(step),
      does: step.does.slice(0, 16),
      branches: step.branches
        .filter((branch) => branch.step.trim().length > 0)
        .map((branch) => ({
          when:
            branch.flag.trim().length > 0
              ? [{ flag: branch.flag.trim(), is: branch.is.trim() || undefined }]
              : [],
          step: Math.max(0, Math.round(number(branch.step))),
        }))
        .slice(0, 8),
      at: {
        npcId: Math.max(0, Math.round(number(step.markNpcId))) || undefined,
        objectId: Math.max(0, Math.round(number(step.markObjectId))) || undefined,
        itemId: Math.max(0, Math.round(number(step.markItemId))) || undefined,
        tx: Math.round(number(step.markTx)) || undefined,
        tz: Math.round(number(step.markTz)) || undefined,
        inPlace: Math.max(0, Math.round(number(step.markPlace))) || undefined,
        note: step.note.trim() || undefined,
      },
    })),
  };
}

/** What is wrong with a draft, or nothing when it is ready to be kept. */
export function problemWith(draft: QuestDraft): string | null {
  if (draft.name.trim().length === 0) return 'It needs a name.';
  if (draft.id.length === 0) return 'That name has nothing to make an id out of.';
  if (draft.steps.length === 0) return 'A quest with no steps is not a quest.';
  if (draft.steps.some((step) => step.text.trim().length === 0)) {
    return 'Every step needs a line for the journal.';
  }
  return null;
}

/** A short line saying what a step is waiting for, for the list. */
export function triggerLine(
  step: StepDraft,
  nameOfItem: (id: number) => string,
  nameOfNpc: (id: number) => string,
  nameOfObject: (id: number) => string = (id) => `scenery ${id}`,
): string {
  switch (step.trigger) {
    case 'carry':
      return `Carrying ${step.count} x ${nameOfItem(Number(step.itemId))}`;
    case 'kill':
      return `Killing ${step.count} x ${nameOfNpc(Number(step.npcId))}`;
    case 'level':
      return `${step.skill} at level ${step.level}`;
    case 'flag':
      return `The mark ${step.flag || 'nothing'} being set`;
    case 'deed': {
      const what =
        step.deed === 'firemaking' || step.deed === 'used'
          ? nameOfObject(Number(step.deedOf))
          : nameOfItem(Number(step.deedOf));

      return `${deedLabel(step.deed)} ${step.count} x ${what}`;
    }
    case 'place':
      return `Standing at ${step.areaTx}, ${step.areaTz}`;
    case 'wearing': {
      const wearing = (id: number, on: WornPlace): string =>
        `${nameOfItem(id)}${on === 'belt' ? ' on the belt' : ''}`;

      const worn = [wearing(Number(step.itemId), step.wornOn)];
      for (const also of step.alsoWear) {
        worn.push(`${also.or ? 'or ' : ''}${wearing(Number(also.itemId), also.on)}`);
      }

      return `Wearing ${worn.join(', ')}`;
    }
    default: {
      const also: string[] = [];
      if (Number(step.alsoItem) > 0) also.push(`carrying ${nameOfItem(Number(step.alsoItem))}`);
      if (Number(step.alsoWorn) > 0) also.push(`wearing ${nameOfItem(Number(step.alsoWorn))}`);

      return also.length > 0 ? `A conversation, while ${also.join(' and ')}` : 'A conversation';
    }
  }
}

/**
 * What a deed is called, in the words somebody would use for it.
 *
 * Named by the skill it took: cooked, smithed, chopped. Anything the world
 * gains a skill for gains a word here with it.
 */
export function deedLabel(deed: string): string {
  const said: Readonly<Record<string, string>> = {
    cooking: 'Cooking',
    smithing: 'Smithing',
    crafting: 'Crafting',
    firemaking: 'Lighting',
    woodcutting: 'Chopping',
    mining: 'Mining',
    fishing: 'Catching',
    used: 'Using',
  };

  return said[deed] ?? 'Doing';
}

/**
 * Which sort of thing a step's mark points at.
 *
 * Worked out from what has been filled in rather than kept as a field of its
 * own: the mark has always been "whichever of these is set", and adding a word
 * saying which would be a second answer to the same question.
 */
export function markKindOf(step: StepDraft): string {
  if (Number(step.markPlace) > 0) return 'place';
  if (Number(step.markNpcId) > 0) return 'npc';
  if (Number(step.markObjectId) > 0) return 'object';
  if (Number(step.markItemId) > 0) return 'item';
  if (Number(step.markTx) !== 0 || Number(step.markTz) !== 0) return 'tile';

  return 'dynamic';
}

/** Everything the mark could have been, cleared, ready for one of them. */
export function markCleared(kind: string): Partial<StepDraft> {
  return {
    markNpcId: '0',
    markObjectId: '0',
    markItemId: '0',
    markTx: '0',
    markTz: '0',
    markPlace: '0',
    ...(kind === 'tile' ? { markTx: '1', markTz: '1' } : {}),
  };
}
